import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AccountStatus,
  AppButton,
  Box,
  CIPStatus,
  Heading,
  PencilIcon,
  PlanStatuses,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';

import Permissions from '@/common/permissions';
import ROUTES from '@/common/routes';
import { ErrorBoundary } from '@/modules/core/components';
import { useBreadcrumbs, useHasAccess } from '@/modules/core/hooks';
import useEmployerTerminationBanner from '@/modules/core/hooks/useEmployerTerminationBanner';
import { EmployeeCIPVerificationBanner } from '@/modules/employee/Employee/cip';
import { useGetDependentsQuery } from '@/modules/employee/Employee/Dependents/hooks/useDependents.query';
import { EnrollmentModal } from '@/modules/employee/Employee/EnrollmentModal';
import { PlanTypes } from '@/modules/employee/Employee/EnrollmentModal/enrollmentModal.types';
import ExcessContributionWithdrawModal from '@/modules/employee/Employee/ExcessContributionWithdrawModal/ExcessContributionWithdrawModal';
import { useExcessContributionAvailable } from '@/modules/employee/Employee/ExcessContributionWithdrawModal/useExcessContributionAvailable';
import { useEnrollmentsData } from '@/modules/employee/Employee/hooks/useEnrollmentsData';
import { useGetEnrollments } from '@/modules/employee/Employee/hooks/useGetEnrollment';
import useGetEmployeeByIdExtended from '@/modules/employee/Employee/queries/useGetEmployeeByIdExtended.query';
import AccountDetails from '@/modules/employee/EnrollmentDetails/AccountDetails';
import ActivitiesList from '@/modules/employee/EnrollmentDetails/ActivitiesList';
import DisclosureBankMessage from '@/modules/employee/EnrollmentDetails/DisclosureBankMessage';
// import DisclosureButton from '@/modules/employee/EnrollmentDetails/DisclosureButton';
import { Wrapper } from '@/modules/employee/EnrollmentDetails/MyAccounts.styles';
import useGetPolicyStatusesQuery from '@/modules/employee/EnrollmentDetails/queries/useGetPolicyStatuses.query';
import { ButtonHintForTerminatedEmployer } from '@/modules/employer/components/EmployerTermination/ButtonHintForTerminatedEmployer';
import { LockboxType } from '@/modules/employer/components/Lockbox/lockbox.types';
import { usePlanByIdQuery } from '@/modules/plan/PlanSetup/queries';
import { useSearchParams } from '@/modules/transaction/Cards/hooks/useSearchParams';
import formatCurrency from '@/utils/modifiers/formatCurrency';

import { CategorySpendLimitsTable } from './CategorySpendLimitsTable/CategorySpendLimitsTable';
import useCategorySpendLimits from './CategorySpendLimitsTable/hooks/useCategorySpendLimits';
import { EnrollmentDetailsAccountInfo } from './EnrollmentDetailsAccountInfo/EnrollmentDetailsAccountInfo';
import { useSpendLimitsData } from './SpendLimitsTable/hooks/useSpendLimitsData';
import useSpendLimitsPeriod from './SpendLimitsTable/hooks/useSpendLimitsPeriod';
import useGetPersonSpendLimitsQuery from './SpendLimitsTable/queries/useGetPersonSpendLimits.query';
import { SpendLimitsTable } from './SpendLimitsTable/SpendLimitsTable';
import { EnrollmentDetailsToggler } from './EnrollmentDetailsToggler';

const enum EnrollmentDetailsToggleOptions {
  ACTIVITIES = 'Activity & Processing',
  ACCOUNT_INFO = 'Account Information',
  SPEND_LIMITS = 'Spend Limits',
}

const ACTION_SEARCH_PARAM = 'action';

export const EnrollmentDetails: React.FC = () => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [isCombineHSAModalAutoOpen, setIsCombineHSAModalAutoOpen] = useState(false);
  const { id, enrollmentId } = useParams<{ id: string; enrollmentId: string }>();
  const { isExcessContributionAvailable } = useExcessContributionAvailable(id);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [hadSpendLimits, setHadSpendLimits] = useState(false);
  const [hadCategorySpendLimits, setHadCategorySpendLimits] = useState(false);
  const [selectedOption, setSelectedOption] = useState(EnrollmentDetailsToggleOptions.ACTIVITIES);

  const { query: actionParam, removeParams } = useSearchParams(ACTION_SEARCH_PARAM);

  const {
    currentMyAccount,
    isCurrentHSA,
    isLoading: isEnrollmentsLoading,
  } = useGetEnrollments({
    accountId: enrollmentId,
    employeeId: id,
  });
  const { investorsData } = useEnrollmentsData(id);
  const { statuses } = useGetPolicyStatusesQuery(currentMyAccount?.policyId);
  const { showPeriodDropdown, periodOptions, selectedPeriod, handlePeriodChange, isPeriodLoading } =
    useSpendLimitsPeriod({ accountId: enrollmentId });
  const { personSpendLimits, isLoading: isPersonSpendLimitsLoading } = useGetPersonSpendLimitsQuery({
    accountId: enrollmentId,
    periodFrom: selectedPeriod,
  });
  const { data: employeeData } = useGetEmployeeByIdExtended(id);
  const { dependents, isLoading: isDependentsLoading } = useGetDependentsQuery(id);
  const spendLimitsData = useSpendLimitsData({
    personSpendLimits,
    dependents,
    employeeId: id,
    employeeName: employeeData?.fullName,
  });

  const {
    categorySpendLimitsData,
    isCategorySpendLimitsLoading,
    hasCategorySpendLimits,
    showPeriodDropdown: showCategoryPeriodDropdown,
    periodOptions: categoryPeriodOptions,
    selectedPeriod: categorySelectedPeriod,
    handlePeriodChange: handleCategoryPeriodChange,
    isPeriodLoading: isCategoryPeriodLoading,
  } = useCategorySpendLimits({
    accountId: enrollmentId,
  });

  const hasSpendLimits = useMemo(() => {
    const hasData = personSpendLimits && personSpendLimits.length > 0;
    if (hasData && !hadSpendLimits) {
      setHadSpendLimits(true);
    }
    return hasData || hadSpendLimits;
  }, [personSpendLimits, hadSpendLimits]);

  const showCategorySpendLimits = useMemo(() => {
    if (hasCategorySpendLimits && !hadCategorySpendLimits) {
      setHadCategorySpendLimits(true);
    }
    return hasCategorySpendLimits || hadCategorySpendLimits;
  }, [hasCategorySpendLimits, hadCategorySpendLimits]);

  const toggleOptions = useMemo(() => {
    const options = [EnrollmentDetailsToggleOptions.ACTIVITIES, EnrollmentDetailsToggleOptions.ACCOUNT_INFO];
    if (hasSpendLimits || showCategorySpendLimits) {
      options.push(EnrollmentDetailsToggleOptions.SPEND_LIMITS);
    }
    return options;
  }, [hasSpendLimits, showCategorySpendLimits]);
  const isWithdrawExcessContributionAvailable = useMemo(() => {
    return currentMyAccount?.accountStatus === AccountStatus.OPEN;
  }, [currentMyAccount?.accountStatus]);

  const isExcessWithdrawAvailable = useMemo(() => {
    return !!(isExcessContributionAvailable && isWithdrawExcessContributionAvailable && isCurrentHSA);
  }, [isExcessContributionAvailable, isWithdrawExcessContributionAvailable, isCurrentHSA]);

  const isNotVerified = useMemo(
    () =>
      (currentMyAccount?.CIPStatus === CIPStatus.Fail ||
        currentMyAccount?.CIPStatus === CIPStatus.Question) &&
      currentMyAccount?.isCIPRequired,
    [currentMyAccount]
  );

  const lastCoverageEndDate = useMemo(() => {
    const coveragePeriods = currentMyAccount?.coveragePeriods;
    if (coveragePeriods?.length) {
      return coveragePeriods?.[0]?.endDate;
    }
    return null;
  }, [currentMyAccount?.coveragePeriods]);

  const isDisplayedCIPBanner = useMemo(
    () => currentMyAccount?.isCIPRequired,
    [currentMyAccount?.isCIPRequired]
  );
  const { data } = useGetEmployeeByIdExtended(id);

  const isProfileAvailable = useHasAccess([{ permission: Permissions.VIEW_PROFILE }]);
  const { isLimitedUseWhileTerminationInProgress } = useEmployerTerminationBanner();
  const { planStatus } = usePlanByIdQuery(currentMyAccount?.planId);
  const isPlanTerminated = useMemo(() => {
    return planStatus === PlanStatuses.TERMINATED || planStatus === PlanStatuses.TERMINATION_IN_PROGRESS;
  }, [planStatus]);

  const isClaimsDateExpired = useMemo(() => {
    if (!currentMyAccount?.submitClaimsDateEnd) {
      return false;
    }
    const currentDate = dayjs();
    const submitClaimsDateEnd = dayjs(currentMyAccount?.submitClaimsDateEnd);
    return currentDate.startOf('day').diff(submitClaimsDateEnd.startOf('day'), 'day') > 0;
  }, [currentMyAccount?.submitClaimsDateEnd]);

  const isPlanEnrollmentTerminated = useMemo(
    () => isPlanTerminated && isClaimsDateExpired,
    [isPlanTerminated, isClaimsDateExpired]
  );

  const currentTooltipText = useMemo(() => {
    if (isPlanEnrollmentTerminated) {
      return 'Terminated plan enrollments cannot be edited.';
    }
    if (isLimitedUseWhileTerminationInProgress) {
      return 'Terminated Organization cannot edit enrollment';
    }
    return undefined;
  }, [isLimitedUseWhileTerminationInProgress, isPlanEnrollmentTerminated]);

  const breadcrumbAccountTitle = useMemo(() => {
    const accountType = currentMyAccount?.accountType || '';
    const availableAmount = formatCurrency(currentMyAccount?.available || 0);
    const planStartYear = currentMyAccount?.planValidFrom
      ? dayjs(currentMyAccount?.planValidFrom).format('YYYY')
      : '';
    return `${accountType} ${planStartYear} (${availableAmount})`;
  }, [currentMyAccount]);

  useBreadcrumbs(
    [
      {
        route: ROUTES.EMPLOYEES,
        title: '...',
      },
      {
        route: `${ROUTES.EMPLOYEE}/${id}`,
        title: data?.fullName || '',
      },
      {
        route: ROUTES.ENROLLMENT_DETAILS(id, enrollmentId),
        title: breadcrumbAccountTitle || '',
        fullWidth: true,
      },
    ],
    [data?.fullName || '', id, breadcrumbAccountTitle],
    {
      backBtn: true,
    }
  );

  useEffect(() => {
    if (actionParam === LockboxType.EXCESS_CONTRIBUTION && !isEnrollmentsLoading) {
      if (isExcessWithdrawAvailable) {
        setModalVisible(true);
      }
      removeParams([ACTION_SEARCH_PARAM]);
    }
    if (actionParam === LockboxType.COMBINE_HSA) {
      setSelectedOption(EnrollmentDetailsToggleOptions.ACCOUNT_INFO);
      setIsCombineHSAModalAutoOpen(true);
      removeParams([ACTION_SEARCH_PARAM]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionParam, isEnrollmentsLoading, isExcessWithdrawAvailable]);

  return (
    <>
      <Wrapper data-testid="MyAccounts-wrapper">
        {currentMyAccount && (
          <Box margin={{ bottom: 'spacing24' }}>
            <Box direction="row" align="center" justify="between">
              <Heading color="textTitle" level={1} size="regular">
                {currentMyAccount.name}
              </Heading>
              <Box direction="row" width={{ min: 'fit-content' }}>
                <ButtonHintForTerminatedEmployer
                  button={
                    <AppButton
                      buttonType="secondary"
                      color="grey"
                      rightIcon={<PencilIcon />}
                      testId="EditEnrollmentModal-open-modal"
                      onClick={() => setEditModalVisible(true)}
                      disabled={isLimitedUseWhileTerminationInProgress || isPlanEnrollmentTerminated}
                    >
                      Edit Enrollment
                    </AppButton>
                  }
                  isTerminatedEmployer={isLimitedUseWhileTerminationInProgress || isPlanEnrollmentTerminated}
                  hintText={currentTooltipText}
                />
                {isExcessContributionAvailable && isWithdrawExcessContributionAvailable && isCurrentHSA ? (
                  <Box margin={{ left: 'spacing12' }}>
                    <AppButton
                      testId="withdrawExcessContributions"
                      width="250px"
                      onClick={() => setModalVisible(true)}
                      buttonType="secondary"
                      color="grey"
                    >
                      Withdraw Excess Contributions
                    </AppButton>
                    {isModalVisible ? (
                      <ExcessContributionWithdrawModal
                        onSetVisible={setModalVisible}
                        isEditMode
                        accountId={enrollmentId}
                      />
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            </Box>
            <AccountDetails
              id={enrollmentId}
              currentMyAccount={currentMyAccount}
              investorsData={investorsData}
            />
          </Box>
        )}
        {isDisplayedCIPBanner && (
          <EmployeeCIPVerificationBanner isStandaloneMode employeeId={id} accountId={enrollmentId} />
        )}

        {isProfileAvailable && (
          <EnrollmentDetailsToggler
            toggleOptions={toggleOptions}
            selectedOption={selectedOption}
            setSelectedOption={(value: string) => setSelectedOption(value as EnrollmentDetailsToggleOptions)}
          />
        )}
        {selectedOption === EnrollmentDetailsToggleOptions.ACTIVITIES && (
          <ErrorBoundary>
            <ActivitiesList disabled={isNotVerified} key={enrollmentId} id={enrollmentId} title="Activity" />
          </ErrorBoundary>
        )}
        {selectedOption === EnrollmentDetailsToggleOptions.ACCOUNT_INFO && isProfileAvailable && (
          <EnrollmentDetailsAccountInfo
            id={enrollmentId}
            employeeId={id}
            firstName={employeeData?.firstName}
            lastName={employeeData?.lastName}
            currentMyAccount={currentMyAccount}
            autoOpenCombineHSA={isCombineHSAModalAutoOpen}
            onCombineHSAAutoOpenHandled={() => setIsCombineHSAModalAutoOpen(false)}
          />
        )}
        {selectedOption === EnrollmentDetailsToggleOptions.SPEND_LIMITS &&
          isProfileAvailable &&
          hasSpendLimits && (
            <SpendLimitsTable
              dependents={spendLimitsData}
              isLoading={isPersonSpendLimitsLoading || isDependentsLoading}
              showPeriodDropdown={showPeriodDropdown}
              periodOptions={periodOptions}
              selectedPeriod={selectedPeriod}
              onPeriodChange={handlePeriodChange}
              isPeriodLoading={isPeriodLoading}
            />
          )}
        {selectedOption === EnrollmentDetailsToggleOptions.SPEND_LIMITS &&
          isProfileAvailable &&
          showCategorySpendLimits && (
            <CategorySpendLimitsTable
              categories={categorySpendLimitsData}
              isLoading={isCategorySpendLimitsLoading}
              showPeriodDropdown={showCategoryPeriodDropdown}
              periodOptions={categoryPeriodOptions}
              selectedPeriod={categorySelectedPeriod}
              onPeriodChange={handleCategoryPeriodChange}
              isPeriodLoading={isCategoryPeriodLoading}
            />
          )}
        <DisclosureBankMessage isCurrentHSA={isCurrentHSA} />
      </Wrapper>
      {editModalVisible && (
        <EnrollmentModal
          isVisible={editModalVisible}
          onSetVisible={setEditModalVisible}
          enrollment={currentMyAccount}
          statuses={statuses}
          lastCoverageEndDate={lastCoverageEndDate}
          planType={PlanTypes.SAVING}
          isEdit
          isWithoutButton
        />
      )}
    </>
  );
};
